import React, { useState, useEffect } from 'react';
import { Flex, Stack, Text } from '@chakra-ui/layout';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Image, useToast, Spinner } from '@chakra-ui/react';
import { Link, useHistory } from 'react-router-dom';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import ReCAPTCHA from 'react-google-recaptcha';
import { Input, Button, ColorfulBar } from '~/components';
import logo from '~/assets/logo-service-gray.png';
import { ENABLE_RECAPTCHA, RECAPTCHA_KEY, ROUTER_PATHS } from '~/constants';
import { useAuth } from '~/hooks';
import { PrivacyPolicyFooter } from '~/components/PrivacyPolicyFooter';

type SignInFormData = {
  username: string;
  password: string;
};

const signInFormSchema = yup.object().shape({
  username: yup.string().required('Login obrigatório'),
  password: yup.string().required('Senha obrigatória'),
});

export const Login: React.VFC = () => {
  const toast = useToast();
  const { signIn, user } = useAuth();
  const history = useHistory();
  const [recaptcha, setRecaptcha] = useState(false);
  const [loginError, setloginError] = useState(false);
  const [loginErrorMessage, setLoginErrorMessage] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [formData, setFormData] = useState<SignInFormData | undefined>();
  const { register, handleSubmit, reset, formState, getValues } = useForm({
    resolver: yupResolver(signInFormSchema),
  });

  const { errors } = formState;

  const handleSignIn: SubmitHandler<SignInFormData> = async formContent => {
    setIsAuthenticating(true);
    setFormData(formContent);

    if (ENABLE_RECAPTCHA) {
      setRecaptcha(true);
    } else {
      handleCaptcha(formContent);
    }
  };

  const handleSAMLSignIn = () => {
    window.open(ROUTER_PATHS.SAML_LOGIN, '_self');
  };

  async function handleCaptcha(formContent?: SignInFormData) {
    const form = formContent || formData;

    if (form) {
      try {
        await signIn(form);
      } catch (error: any) {
        console.log('ERRO');
        console.error(error);
        if (error.response.status === 412) {
          toast({
            title: 'Erro ao realizar login',
            description: 'Senha expirada',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
          goToExpiredPassword(error.response.data.detail);
        } else {
          setloginError(error.response.status === 400);
          setLoginErrorMessage(error.response.data.detail);

          toast({
            title: 'Erro ao realizar login',
            description:
              error.response.status >= 400
                ? `${error.response.data.detail}`
                : 'Tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
        setIsAuthenticating(false);
        setRecaptcha(false);
      }
    }
  }
  const goToExpiredPassword = (token: string) => {
    history.push(`/recovery-password/${token}/expired`);
  };

  useEffect(() => {
    if (formState.isSubmitted) {
      reset({
        username: getValues().username,
        password: '',
      });
    }
  }, [formState.isSubmitted]);

  useEffect(() => {
    const listener = async (event: KeyboardEvent) => {
      if (event.code === 'Enter' || event.code === 'NumpadEnter') {
        console.log('Enter key was pressed. Run your function.');
        document.getElementsByTagName('button')[0].click();
      }
    };
    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
    };
  }, []);

  return (
    <Flex w="100vw" h="100vh" flexDir="column">
      <ColorfulBar />
      <Flex justify="center" p="2" pt={16} h="100%">
        <Flex flexDir="column" align="center">
          <Image src={logo} maxW="200px" mb="6" w="170px" />
          <Text fontSize="1.5rem" fontWeight="medium">
            Portal de Locações
          </Text>
          <Stack as="form" w={300} gridGap="2" mt="8">
            {loginError && (
              <Flex justify="center">
                <Text fontSize="14px">{loginErrorMessage}</Text>
              </Flex>
            )}
            <Input
              name="username"
              type="text"
              label="Login ou e-mail"
              register={register}
              error={errors.username}
            />
            <Input
              name="password"
              type="password"
              autoComplete="off"
              label="Senha"
              register={register}
              error={errors.password}
            />
            <Flex justify="right">
              <Link to={ROUTER_PATHS.FORGOT_PASSWORD}>
                <Text fontSize="14px" color="gray.900">
                  Esqueci a senha
                </Text>
              </Link>
            </Flex>
            <Flex h="1" />
            <Button
              isLoading={formState.isSubmitting}
              onClick={handleSubmit(handleSignIn)}
            >
              {isAuthenticating ? <Spinner /> : 'Entrar'}
            </Button>
            <Flex h="1" />
            <Button variant="secondary" onClick={handleSAMLSignIn}>
              Entrar com Azure
            </Button>
            {recaptcha && (
              <ReCAPTCHA
                sitekey={String(RECAPTCHA_KEY)}
                onChange={() => handleCaptcha()}
              />
            )}
          </Stack>
          <PrivacyPolicyFooter isPortal={false} />
        </Flex>
      </Flex>
    </Flex>
  );
};
